<template>
  <section id="install-guide" class="install-guide-section section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">GAC Code 安装配置指南</h2>
        <p class="section-subtitle">
          🚀 三步快速开始使用 GAC Code，享受国内稳定的 Claude 编程助手体验
        </p>
      </div>

      <div class="steps-container">
        <!-- 步骤1: GAC账号注册 -->
        <div class="step-card">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3 class="step-title">📝 注册 GAC 账号</h3>
            <p class="step-description">首先需要在 GAC 官网注册账号并获取 API 密钥</p>
            <div class="step-actions">
              <a href="https://gaccode.com/" target="_blank" class="btn btn-primary">
                前往 GAC 官网注册
              </a>
              <p class="step-note">注册后在用户中心获取 API 密钥</p>
            </div>
          </div>
        </div>

        <!-- 步骤2: 安装 Claude Code -->
        <div class="step-card">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3 class="step-title">⚡ 安装 Claude Code</h3>
            <p class="step-description">安装官方 Claude Code CLI 工具</p>
            
            <div class="platform-tabs">
              <button 
                class="tab-button" 
                :class="{ active: activeTab === 'windows' }"
                @click="activeTab = 'windows'"
              >
                Windows
              </button>
              <button 
                class="tab-button" 
                :class="{ active: activeTab === 'mac' }"
                @click="activeTab = 'mac'"
              >
                macOS
              </button>
              <button 
                class="tab-button" 
                :class="{ active: activeTab === 'linux' }"
                @click="activeTab = 'linux'"
              >
                Linux
              </button>
            </div>

            <div class="tab-content">
              <div v-if="activeTab === 'windows'" class="install-commands">
                <div class="command-block">
                  <h4>使用 npm 安装：</h4>
                  <code class="command">npm install -g @anthropic-ai/claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('npm install -g @anthropic-ai/claude-code')">
                    复制
                  </button>
                </div>
                <div class="command-block">
                  <h4>或使用 yarn 安装：</h4>
                  <code class="command">yarn global add @anthropic-ai/claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('yarn global add @anthropic-ai/claude-code')">
                    复制
                  </button>
                </div>
              </div>

              <div v-if="activeTab === 'mac'" class="install-commands">
                <div class="command-block">
                  <h4>使用 Homebrew 安装（推荐）：</h4>
                  <code class="command">brew install claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('brew install claude-code')">
                    复制
                  </button>
                </div>
                <div class="command-block">
                  <h4>或使用 npm 安装：</h4>
                  <code class="command">npm install -g @anthropic-ai/claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('npm install -g @anthropic-ai/claude-code')">
                    复制
                  </button>
                </div>
                <div class="mac-note">
                  <p><strong>注意：</strong>如果遇到权限问题，可能需要使用 <code>sudo</code> 命令</p>
                </div>
              </div>

              <div v-if="activeTab === 'linux'" class="install-commands">
                <div class="command-block">
                  <h4>使用 npm 安装：</h4>
                  <code class="command">sudo npm install -g @anthropic-ai/claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('sudo npm install -g @anthropic-ai/claude-code')">
                    复制
                  </button>
                </div>
                <div class="command-block">
                  <h4>或下载二进制文件：</h4>
                  <code class="command">curl -L https://github.com/anthropics/claude-code/releases/latest/download/claude-code-linux -o claude-code</code>
                  <button class="copy-btn" @click="copyToClipboard('curl -L https://github.com/anthropics/claude-code/releases/latest/download/claude-code-linux -o claude-code')">
                    复制
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 步骤3: 配置 GAC 服务 -->
        <div class="step-card">
          <div class="step-number">3</div>
          <div class="step-content">
            <h3 class="step-title">🔧 配置 GAC 服务</h3>
            <p class="step-description">配置环境变量和Claude配置文件</p>
            
            <div class="config-section">
              <h4>方法一：设置环境变量（推荐）</h4>
              <div class="config-steps">
                <div class="config-step">
                  <h5>Windows 配置：</h5>
                  <ol>
                    <li>按 <kbd>Win + R</kbd> 输入 <code>sysdm.cpl</code> 打开系统属性</li>
                    <li>点击"高级" → "环境变量"</li>
                    <li>在用户变量中新建以下变量：</li>
                  </ol>
                  <div class="env-vars">
                    <div class="env-var">
                      <strong>变量名：</strong> <code>ANTHROPIC_API_KEY</code><br>
                      <strong>变量值：</strong> <code>你从GAC获取的API密钥</code>
                    </div>
                    <div class="env-var">
                      <strong>变量名：</strong> <code>ANTHROPIC_BASE_URL</code><br>
                      <strong>变量值：</strong> <code>https://gaccode.com/claudecode</code>
                      <button class="copy-btn" @click="copyToClipboard('https://gaccode.com/claudecode')">
                        复制
                      </button>
                    </div>
                  </div>
                </div>

                <div class="config-step">
                  <h5>macOS/Linux 配置：</h5>
                  <p>在终端中执行以下命令：</p>
                  <div class="command-block">
                    <code class="command">export ANTHROPIC_API_KEY="你的API密钥"</code>
                    <code class="command">export ANTHROPIC_BASE_URL="https://gaccode.com/claudecode"</code>
                  </div>
                  <p>要永久保存，请将上述命令添加到 <code>~/.bashrc</code> 或 <code>~/.zshrc</code> 文件中</p>
                </div>
              </div>
            </div>

            <div class="config-section">
              <h4>方法二：配置文件设置</h4>
              <p>编辑 Claude 配置文件：<code>~/.claude.json</code></p>
              <div class="config-example">
                <pre><code>{
  "installMethod": "unknown",
  "autoUpdates": true,
  "apiKey": "sk-ant-oat01-xxxxxxx",
  "baseUrl": "https://gaccode.com/claudecode",
  "firstStartTime": "2025-01-01T00:00:00.000Z",
  "userID": "xxxxx",
  "customApiKeyResponses": {
    "approved": ["最后20个字符"]
  }
}</code></pre>
                <button class="copy-btn config-copy" @click="copyConfigTemplate()">
                  复制配置模板
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 步骤4: 完成配置 -->
        <div class="step-card">
          <div class="step-number">4</div>
          <div class="step-content">
            <h3 class="step-title">🎉 完成配置，开始使用</h3>
            <p class="step-description">恭喜！您已完成 GAC Code 的安装和配置</p>
            
            <div class="completion-steps">
              <div class="completion-step">
                <div class="step-icon">🔄</div>
                <div class="step-info">
                  <h5>重启生效</h5>
                  <p>如果您使用了环境变量配置方式，请<strong>重启电脑</strong>使环境变量生效</p>
                </div>
              </div>
              
              <div class="completion-step">
                <div class="step-icon">🚀</div>
                <div class="step-info">
                  <h5>开始使用</h5>
                  <p>重启后即可在任意目录的终端中直接使用 <code>claude-code</code> 命令</p>
                </div>
              </div>
              
              <div class="completion-step">
                <div class="step-icon">💡</div>
                <div class="step-info">
                  <h5>使用建议</h5>
                  <p>建议在项目根目录运行 Claude Code，这样可以更好地理解项目结构</p>
                </div>
              </div>
            </div>

            <div class="success-message">
              <h5>🎉 配置完成！</h5>
              <p>现在您可以享受 GAC 提供的稳定 Claude Code 服务了！有任何问题请联系客服支持。</p>
              <div class="next-actions">
                <a href="https://gaccode.com/" target="_blank" class="btn btn-primary">
                  GAC 用户中心
                </a>
                <a href="#pricing" class="btn btn-secondary" @click="$parent.setCurrentPage('home')">
                  查看套餐
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 常见问题 -->
      <div class="faq-section">
        <h3 class="faq-title">🤔 常见问题</h3>
        <div class="faq-grid">
          <div class="faq-item">
            <h4>Q: 如何获取 API 密钥？</h4>
            <p>A: 前往 <a href="https://gaccode.com/" target="_blank">GAC官网</a> 注册账号，在用户中心即可获取API密钥。</p>
          </div>
          <div class="faq-item">
            <h4>Q: 遇到网络连接问题怎么办？</h4>
            <p>A: 请检查 ANTHROPIC_BASE_URL 是否正确设置为 https://gaccode.com/claudecode</p>
          </div>
          <div class="faq-item">
            <h4>Q: API 密钥无效怎么办？</h4>
            <p>A: 请确认在GAC官网购买了对应套餐，并复制了正确的API密钥。</p>
          </div>
          <div class="faq-item">
            <h4>Q: 需要技术支持？</h4>
            <p>A: 联系客服邮箱：<a href="mailto:1277623709@qq.com">1277623709@qq.com</a></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'InstallGuideSection',
  data() {
    return {
      activeTab: 'windows'
    }
  },
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // 简单的反馈，可以后续优化为toast提示
        alert('已复制到剪贴板！');
      }).catch(() => {
        // 降级方案
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('已复制到剪贴板！');
      });
    },
    copyConfigTemplate() {
      const template = `{
  "installMethod": "unknown",
  "autoUpdates": true,
  "apiKey": "sk-ant-oat01-xxxxxxx",
  "baseUrl": "https://gaccode.com/claudecode",
  "firstStartTime": "2025-01-01T00:00:00.000Z",
  "userID": "xxxxx",
  "customApiKeyResponses": {
    "approved": ["最后20个字符"]
  }
}`;
      this.copyToClipboard(template);
    }
  }
}
</script>

<style scoped>
.install-guide-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.steps-container {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.step-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.step-number {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

.step-description {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

.step-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-note {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

/* 平台选项卡 */
.platform-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 24px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
}

.tab-button {
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button.active {
  background: white;
  color: #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.install-commands {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.command-block {
  position: relative;
}

.command-block h4 {
  color: #1e293b;
  margin-bottom: 8px;
  font-size: 1rem;
}

.command {
  background: #1e293b;
  color: #e2e8f0;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 0.875rem;
  display: block;
  margin-bottom: 8px;
  word-break: break-all;
}

.copy-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn:hover {
  background: #1d4ed8;
}

.mac-note {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 12px;
  margin-top: 16px;
}

.mac-note code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
}

/* 配置部分 */
.config-section {
  margin-bottom: 32px;
}

.config-section h4 {
  color: #1e293b;
  margin-bottom: 16px;
  font-size: 1.125rem;
}

.config-steps {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.config-step h5 {
  color: #1e293b;
  margin-bottom: 12px;
}

.config-step ol {
  margin-bottom: 16px;
  padding-left: 20px;
}

.config-step li {
  margin-bottom: 8px;
  color: #64748b;
}

.env-vars {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.env-var {
  position: relative;
}

.env-var code {
  background: #1e293b;
  color: #e2e8f0;
  padding: 4px 8px;
  border-radius: 4px;
}

.config-example {
  position: relative;
  background: #1e293b;
  border-radius: 8px;
  overflow: hidden;
}

.config-example pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
}

.config-example code {
  color: #e2e8f0;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 0.875rem;
  line-height: 1.6;
}

.config-copy {
  position: absolute;
  top: 12px;
  right: 12px;
}

/* 完成配置部分 */
.completion-steps {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

.completion-step {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.step-icon {
  font-size: 2rem;
  line-height: 1;
  flex-shrink: 0;
}

.step-info {
  flex: 1;
}

.step-info h5 {
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.step-info p {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.step-info code {
  background: #1e293b;
  color: #e2e8f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.875rem;
}

.success-message {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  border: 1px solid #16a34a;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.success-message h5 {
  color: #15803d;
  margin-bottom: 8px;
}

.success-message p {
  color: #166534;
  margin: 0 0 20px 0;
}

.next-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* FAQ部分 */
.faq-section {
  margin-top: 80px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.faq-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 40px;
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.faq-item {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.faq-item h4 {
  color: #1e293b;
  margin-bottom: 12px;
  font-size: 1rem;
}

.faq-item p {
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.faq-item a {
  color: #3b82f6;
  text-decoration: none;
}

.faq-item a:hover {
  text-decoration: underline;
}

kbd {
  background: #f1f5f9;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 6px;
  font-family: monospace;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .step-card {
    flex-direction: column;
    padding: 24px;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .faq-grid {
    grid-template-columns: 1fr;
  }
  
  .config-steps {
    gap: 16px;
  }
  
  .platform-tabs {
    flex-direction: column;
  }
}
</style>